import {socialAuthIntegrationSchema} from '@practicegenius/domain/src/data/integrations';
import {z} from 'zod';
import {replaceSubdomainInUrl} from '@practicegenius/pg-util/src/UrlUtils';
import qs from 'qs';
import {booleanSchema} from '@practicegenius/pg-util/src/ZodUtils';
import {authPerspectiveSchema} from './authPerspective';
import {memberDestinationSchema} from './memberDestination';

/** Subdomain of Central Identity Service */
const ID_SUBDOMAIN = 'id';

export const idDotLoginRoutes = {
  index: '/login',
  social: {
    login: '/login/social_provider/:provider',
    callback: '/login/social_provider/callback',
  },
};

export const idDotRegistrationRoutes = {
  authenticate: '/registration/authenticate',
};

export const idDotApiRoutesForLogin = {
  authRefresh: '/api/s2s/authRefresh',
  confirmLogin: '/api/s2s/confirmLogin',
};

export const idDotSharedAuthenticationRoutes = {
  /** Handles authenticating a downstream app according to query params and redirecting to it */
  authenticateToApp: '/app/authenticateToApp',
};

export const idDotLogoutRoutes = {
  logout: {
    index: '/app/logout',
    initiate: '/app/logout/initiate',
  },
};

export const idDotUserRoutes = {
  profile: '/app/user/profile',
  linkedUserProfile: '/app/user/linked-profile/:linkedUserId',
  linkedFamily: '/app/user/linked-family',
};

const loginErrorQueryParamSchema = z.union([
  z.object({
    errorType: z.literal('unregistered_social_account'),
    provider: socialAuthIntegrationSchema,
  }),
  z.object({
    errorType: z.literal('generic'),
  }),
  z.object({
    errorType: z.literal('registration_not_finished'),
    token: z.string(),
    viaSocial: booleanSchema.optional(),
  }),
  z.object({}),
]);

export const loginRejectAfterSuccessReasonSchema = z.literal('denied');
export type LoginRejectAfterSuccessReason = z.infer<
  typeof loginRejectAfterSuccessReasonSchema
>;
export const idDotLoginQueryParamsSchema = z.intersection(
  loginErrorQueryParamSchema,
  z.object({
    referrer: z.string().optional(),
    loginPerspective: authPerspectiveSchema.optional(),
    rejectAfterSuccess: loginRejectAfterSuccessReasonSchema.optional(),
    hideBackButton: booleanSchema.optional(),
    memberDestination: memberDestinationSchema.optional(),
  }),
);

export const buildIdBaseUrlFromCurrentUrl = (currentUrl: string) =>
  replaceSubdomainInUrl(currentUrl, ID_SUBDOMAIN);

/**
 * Generic function to build ID dot URLs for any route
 */
export const buildIdDotUrl = (
  /** Current URL from window or host header */
  currentUrl: string,
  /**
   * Route in id-dot. If this contains query params they will be merged with the queryParams parameter if any are specified there.
   * If there are duplicated params between the route and the queryParams parameter, the queryParams parameter will take precedence.
   */
  route: string,
  queryParams: Record<string, string | number | boolean> = {},
): string => {
  const idUrl = buildIdBaseUrlFromCurrentUrl(currentUrl);
  // eslint-disable-next-line prefer-const -- one of the members is reassigned
  let [routeWithoutQuery, queryStringFromRoute] = route.split('?');

  // strip leading slash if present
  routeWithoutQuery = routeWithoutQuery.startsWith('/')
    ? routeWithoutQuery.slice(1)
    : routeWithoutQuery;
  idUrl.pathname = routeWithoutQuery;

  idUrl.search = qs.stringify({
    ...(queryStringFromRoute ? qs.parse(queryStringFromRoute) : {}),
    ...queryParams,
  });

  return idUrl.toString();
};

export type IdDotLoginQueryParams = z.infer<typeof idDotLoginQueryParamsSchema>;

export const buildIdLoginUrlFromCurrentUrl = (
  currentUrl: string,
  params: IdDotLoginQueryParams,
): string => {
  const loginUrl = buildIdBaseUrlFromCurrentUrl(currentUrl);
  loginUrl.pathname = idDotLoginRoutes.index;
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      const serializedValue =
        typeof value === 'boolean' ? value.toString() : value;
      loginUrl.searchParams.set(key, serializedValue);
    }
  }
  return loginUrl.toString();
};

export const idDotLogoutQueryParamsSchema = z.object({
  /** Perspective to show on the login screen after logout */
  loginPerspective: authPerspectiveSchema.optional(),
  /** Redirect to this URL instead of the login page after logout */
  redirect: z.string().optional(),
});
export type IdDotLogoutQueryParams = z.infer<
  typeof idDotLogoutQueryParamsSchema
>;

export const buildIdLogoutUrlFromCurrentUrl = (
  currentUrl: string,
  params: IdDotLogoutQueryParams = {},
): string => {
  const logoutUrl = buildIdBaseUrlFromCurrentUrl(currentUrl);
  logoutUrl.pathname = idDotLogoutRoutes.logout.index;
  logoutUrl.search = qs.stringify(params);
  return logoutUrl.toString();
};

export const buildIdLogoutInitiateUrlFromCurrentUrl = (
  currentUrl: string,
  params: IdDotLogoutQueryParams = {},
): string => {
  const logoutUrl = buildIdBaseUrlFromCurrentUrl(currentUrl);
  logoutUrl.pathname = idDotLogoutRoutes.logout.initiate;
  logoutUrl.search = qs.stringify(params);
  return logoutUrl.toString();
};

export const idDotRegistrationAuthCodeParamsSchema = z.object({
  registrationAuthCode: z.string(),
  referrer: z.string().optional(),
});
export type IdDotRegistrationAuthCodeParams = z.infer<
  typeof idDotRegistrationAuthCodeParamsSchema
>;

export const buildIdRegistrationUrl = (
  currentUrl: string,
  params: IdDotRegistrationAuthCodeParams,
): string => {
  const registrationUrl = buildIdBaseUrlFromCurrentUrl(currentUrl);
  registrationUrl.pathname = idDotRegistrationRoutes.authenticate;
  registrationUrl.search = qs.stringify(params);
  return registrationUrl.toString();
};
